import React, { useState, useMemo, useCallback } from 'react';
import { useLocationInfo, useUserInfo } from '@/context/PageStateContext';
import Countdown from './Countdown';
import AppConfirmModal from './AppConfirmModal';
import { checkWayneSupplyRecallable } from '@/api/coupon';
import { useCouponRecallInfo, usePageState } from '@/context/PageStateContext';
import {
  CouponWallItem,
  CouponStatus,
  CouponOperation,
  JUMP_LINK_TYPE,
  CouponWallTab,
  CouponSupply,
  CouponWallScene,
} from '@/types/coupon-wall';
import {
  JumpTypeEnum,
  JumpMethodsInMtWxApp,
  JumpMethodsInMtApp,
  JumpMethodsInBrowser,
} from '@/lib/jumpPage';
import { RUNTIME_ENV_TYPE } from '@/lib/constant';
import { useTracker } from '@/context/TrackerContext';
import { useAppEnv } from '@/hooks/useAppEnv';
import { parsePrice, ge, eq, reportCouponWallGoToUseSuccessRate, GoToUseType } from './utils';
import { Level, reportError } from '@/lib/owl';
import Exposure from '@/components/exposure';
import { COUPON_WALL_CHECK_WAYNE_SRC_MAP, CouponWallStatus } from './constants';
import { COUPON_WALL_BID_MC, COUPON_WALL_BID_MV } from '@/track/coupon-wall';

// 券卡片样式
export enum CouponCardStyle {
  DEFAULT = 'default', // 默认样式
  SINGLE = 'single', // 单券样式
  COMPACT = 'compact', // 紧凑样式（当券墙展示两行券时，采用这种样式，券高度更矮）
}

/**
 * 单个券卡片组件
 */
const CouponCard: React.FC<{
  coupon: CouponWallItem;
  couponIndex: number;
  tab?: CouponWallTab;
  redStatus: CouponWallStatus;
  currentTime: number;
  showCouponValue: boolean;
  hasUnreceivedCoupons: boolean;
  distributorChannel: number;
  scene: CouponWallScene; // 用于控制不同页面的接口入参
  style?: CouponCardStyle; // 控制展示 单券样式 还是 默认券卡片样式
  enableFitFewCoupon?: boolean; // 是否开启少量券样式适配，默认不开启
  autoReceiveCountdown?: number; // 自动领取倒计时，单券样式使用
  onUpdateCouponSupply?: (couponSupply: CouponSupply) => void; // 更新券供给信息
  onReceiveCoupon?: () => void; // 领券
}> = ({
  coupon,
  couponIndex,
  tab,
  redStatus,
  currentTime,
  showCouponValue,
  hasUnreceivedCoupons,
  distributorChannel,
  scene,
  style = CouponCardStyle.DEFAULT,
  enableFitFewCoupon = false, // 是否开启少量券样式适配，默认不开启
  autoReceiveCountdown,
  onUpdateCouponSupply = () => {},
  onReceiveCoupon = () => {},
}) => {
  const { completeGetUnpl, waitForUnpl, jumpLoading, setJumpLoading } = usePageState();
  const { locationInfo } = useLocationInfo();
  const { userInfo } = useUserInfo();
  const { actualCityInfo } = locationInfo || {};
  const { setCouponRecallInfo } = useCouponRecallInfo();
  const tracker = useTracker();
  const appEnv = useAppEnv();
  const { data } = coupon || {};
  const { 
    couponValue, 
    actSysSrc,
    priceLimit,
    couponName, 
    couponEndTime, 
    couponViewId,
    operation,
    couponSupply,
    couponConfigId,
    isFestivalCoupon
  } = data || {};
  // 券卡埋点参数
  const couponValLab = useMemo(() => {
    return {
      tab_name: tab?.tabName || '',
      user_coupon_id: couponViewId || '',
      red_status: redStatus,
      coupon_channel: distributorChannel,
      coupon_index: couponIndex,
      discount_coupon_config_id: couponConfigId,
      act_sys_src: actSysSrc,
      exposure_num: style === CouponCardStyle.COMPACT ? 1 : 0, // 展示券样式：1-两行券，0-一行券
    };
  }, [actSysSrc, tab, style, couponViewId, redStatus]);
  // 去使用按钮埋点参数
  const useButtonValLab = useMemo(() => {
    return {
      wm_item_type: 'wm_coupon', // 写死：wm_coupon
      user_coupon_id: couponViewId || '',
      tab_name: tab?.tabName || '',
      coupon_channel: distributorChannel,
      coupon_index: couponIndex,
      discount_coupon_config_id: couponConfigId,
      act_sys_src: actSysSrc,
      exposure_num: style === CouponCardStyle.COMPACT ? 1 : 0, // 展示券样式：1-两行券，0-一行券
    };
  }, [actSysSrc, tab, style, couponViewId, redStatus]);
  const [showAppConfirmModal, setShowAppConfirmModal] = useState<boolean>(false); // 唤端确认弹窗显示状态

  // 处理唤端确认弹窗关闭
  const handleCloseModal = useCallback(() => {
    setShowAppConfirmModal(false);
  }, []);
  
  
  // 判断是否已过期
  const [isExpired, setIsExpired] = useState<boolean>(() => {
    if (!currentTime || !couponEndTime) return false;
    return couponEndTime <= currentTime;
  });

  // 计算券的状态
  const couponStatus = useMemo(() => {
    if (!couponViewId) {
      return CouponStatus.NOT_RECEIVED; // 未领取
    }
    
    if (isExpired) {
      return CouponStatus.EXPIRED;
    }
    
    // 存在跳转链接，或券可以召回供给，展示去使用，否则展示已领取
    return (!!operation?.jumpLink || couponSupply?.recallable) ? CouponStatus.CAN_USE : CouponStatus.RECEIVED;
  }, [couponViewId, isExpired, operation]);

  // 判断是否显示门槛、按钮、倒计时
  // 若存在可领取的券，则不展示券额下面的门槛、按钮、倒计时
  const shouldShowDetails = !hasUnreceivedCoupons

  // 将分转换为元
  const couponValueInYuan = useMemo(() => {
    if (!couponValue) return 0;
    return couponValue / 100;
  }, [couponValue]);

  // 格式化券金额显示
  const formatCouponValue = useMemo(() => {
    const integerPart = Math.floor(couponValueInYuan);
    const decimalPart = couponValueInYuan - integerPart;
    
    // 整数部分超过99的只显示整数
    if (integerPart > 99) {
      return String(integerPart);
    }
    
    // 整数部分小于等于99的如果有小数，展示一位小数
    if (decimalPart > 0) {
      return couponValueInYuan.toFixed(1);
    }
    
    // 没有小数，直接返回整数
    return String(integerPart);
  }, [couponValueInYuan]);

  // 计算显示的券面额
  const displayValue = useMemo(() => {
    const isReceived = couponStatus !== CouponStatus.NOT_RECEIVED;
    
    // showCouponValue 为 true：已领取和未领取都展示券面额
    if (showCouponValue) {
      return formatCouponValue;
    }
    
    // showCouponValue 为 false：
    // - 未领取：显示问号 - 已领取：显示券面额
    return isReceived ? formatCouponValue : '?';
  }, [showCouponValue, formatCouponValue, couponStatus]);

  // 计算数字个数（用于调整字体大小）
  const digitCount = useMemo(() => {
    // 如果是问号，返回0（保持原字体大小）
    if (displayValue === '?') return 0;
    // 将数字转换为字符串，计算字符个数
    return String(displayValue).length;
  }, [displayValue]);

  // 判断是否需要使用小字体（数字个数 >= 4）
  const useSmallFont = digitCount >= 4;

  // 格式化门槛显示（从分转为元，0显示无门槛，大于0取小数点后一位或两位）
  const priceLimitText = useMemo(() => {
    let limitText : string = '';
    if(ge(priceLimit, 0)){
        if(eq(priceLimit, 0)){
            limitText = '无门槛'
        }else{
            limitText = '满' + parsePrice(priceLimit).priceText+'元可用'
        }
    }
    return limitText;
  }, [priceLimit]);

  // 处理过期回调
  const handleExpired = () => {
    setIsExpired(true);
  };

  // 发送全局消息：将当前的韦恩ID以及券的信息，通过 pageState 传递给其他组件
  // resCouponSupply: 韦恩供给判定接口返回的 couponSupply，如果存在该参数，则在更新 couponRecallInfo 时，将该参数合并到 data 中
  const sendGlobalMessage = (resCouponSupply?: CouponSupply) => {
    const currentCouponSupply = resCouponSupply || couponSupply;
    if (!currentCouponSupply?.segmentIdList?.length || !currentCouponSupply?.wayneActId || !data) {
      // 上报券去使用-失败-无数据片ID、券ID或数据
      reportCouponWallGoToUseSuccessRate(false, GoToUseType.RecallSupply, "noSegmentIdOrWayneActId");
      return;
    }

    setCouponRecallInfo({
      ...data,
      couponSupply: currentCouponSupply,
    });
    // 上报券去使用-成功-供给列表召回
    reportCouponWallGoToUseSuccessRate(true, GoToUseType.RecallSupply);
  };


  /**
 * 美小内跳转处理函数
*/
const handleJumpInMtMini = (operation: CouponOperation) => {
  const { jumpMiniProgram } = JumpMethodsInMtWxApp;
  let url = operation.jumpLink;
  if (operation.jumpLinkType === JUMP_LINK_TYPE.H5) {
    // 跳转至外卖容器，同步登录态：https://km.sankuai.com/collabpage/1925522215
    url = `/waimai-mt/pages/web-view/web-view?type=REDIRECT&webviewUrl=${operation.jumpLink}`;
  }
  jumpMiniProgram({
    jumpLink: url,
    jumpType: JumpTypeEnum.MT_WXAPP,
    source: 'couponWall',
    onSuccess() {
      // 上报券去使用-成功-跳转
      reportCouponWallGoToUseSuccessRate(true, GoToUseType.JumpLink);
    },
    onFail() {
      // 上报券去使用-失败-跳转
      reportCouponWallGoToUseSuccessRate(false, GoToUseType.JumpLink, "jumpFail");
    },
  });
};


/**
 * 美团APP跳转处理函数
*/
const handleJumpInMtApp = (operation: CouponOperation) => {
  // 美团APP内不支持跳转美团小程序（业务不需要）
  if (operation.jumpLinkType === JUMP_LINK_TYPE.MT_MINI) {
    // 上报券去使用-失败-跳转-不支持跳转至小程序
    reportCouponWallGoToUseSuccessRate(false, GoToUseType.JumpLink, "notSupportedJumpType");
    return;
  }
  const { jumpScheme } = JumpMethodsInMtApp;
  jumpScheme({
    jumpLink: operation.jumpLink,
    jumpType: JumpTypeEnum.MT_APP,
    source: 'couponWall',
    sourceBid: COUPON_WALL_BID_MC.USE_BUTTON,
    onSuccess() {
      // 上报券去使用-成功-跳转
      reportCouponWallGoToUseSuccessRate(true, GoToUseType.JumpLink);
    },
    onFail() {
      // 上报券去使用-失败-跳转
      reportCouponWallGoToUseSuccessRate(false, GoToUseType.JumpLink, "jumpFail");
    },
  });
};

  /**
   * 唤端确认弹窗处理函数
  */
  const handleJumpInBrowser = (operation: CouponOperation) => {
    // 不支持跳转至小程序（业务侧暂时不需要支持跳转至小程序）
    if (operation.jumpLinkType === JUMP_LINK_TYPE.MT_MINI) {
      // 上报券去使用-失败-跳转-不支持跳转至小程序
      reportCouponWallGoToUseSuccessRate(false, GoToUseType.JumpLink, "notSupportedJumpType");
      return;
    }
    if (operation.jumpLinkType === JUMP_LINK_TYPE.H5) {
      JumpMethodsInBrowser.jumpH5({
        jumpLink: operation.jumpLink,
        jumpType: JumpTypeEnum.H5,
        source: 'couponWall',
        onSuccess() {
          // 上报券去使用-成功-跳转
          reportCouponWallGoToUseSuccessRate(true, GoToUseType.JumpLink);
        },
        onFail() {
          // 上报券去使用-失败-跳转
          reportCouponWallGoToUseSuccessRate(false, GoToUseType.JumpLink, "jumpFail");
        },
      });
      return;
    }
    // 打开唤端确认弹窗
    setShowAppConfirmModal(true);
  };

  // 执行跳转
  const executeJump = async () => {
    if (!operation?.jumpLink) {
      // 上报券去使用-失败-无跳转链接
      reportCouponWallGoToUseSuccessRate(false, GoToUseType.JumpLink, "noJumpLink");
      return;
    }

    // sessionId 未绑定完成时，展示全局 Loading 等待
    if (jumpLoading) return;
    if (!completeGetUnpl) {
      setJumpLoading(true);
      await waitForUnpl();
      setJumpLoading(false);
    }

    switch (appEnv.runtimeEnv) {
      case RUNTIME_ENV_TYPE.MT_MINI:
        handleJumpInMtMini(operation);
        break;
      case RUNTIME_ENV_TYPE.MT_APP:
        handleJumpInMtApp(operation);
        break;
      case RUNTIME_ENV_TYPE.WX_BROWSER:
      case RUNTIME_ENV_TYPE.BROWSER:
        handleJumpInBrowser(operation);
        break;
      default:
        break;
    }
  };

  // 处理跳转
  const handleJump = async () => {
    // 上报埋点-券去使用点击
    tracker?.moduleClick(COUPON_WALL_BID_MC.USE_BUTTON, useButtonValLab);
    // 1. 判断 couponSupply 是否有值
    if (couponSupply) {
      // 2. 判断 couponSupply.recallable
      if (couponSupply.recallable === true) {
        // 若为 true，直接发送全局消息
        sendGlobalMessage();
        return;
      } else if (couponSupply.recallable === false) {
        // 若为 false，走到第三步（执行跳转）
        executeJump();
        return;
      }
    }

    // 若 couponSupply 为空或者 couponSupply.recallable 为空且 CouponSupply.wayneActId 有值，走到第4步
    if (couponSupply && (couponSupply.recallable === null || couponSupply.recallable === undefined) && couponSupply.wayneActId) {
      try {
        // 4. post 请求，请求链接 /fulishemini/couponActivity/checkWayneSupplyRecallable
        // 构建 headers：只有当 token 存在且非空时才添加 token header
        const headers: Record<string, string> = {};
        if (userInfo?.token && userInfo.token.trim() !== '') {
          headers.token = userInfo.token;
        }
        
        const response = await checkWayneSupplyRecallable({
          src: COUPON_WALL_CHECK_WAYNE_SRC_MAP[scene],
          wayneActIdList: [couponSupply.wayneActId],
          wm_ctype: appEnv.cType,
          wm_actual_latitude: actualCityInfo?.wm_lat || 0,
          wm_actual_longitude: actualCityInfo?.wm_lng || 0,
        }, {
          ...(Object.keys(headers).length > 0 ? { headers } : {}),
        });

        if (response && response.data) {
          // 判断返回结果中是否有对应 wayneActId
          const { recallableWayneActIdList, recallableWayneList } = response.data;
          if (recallableWayneActIdList && recallableWayneActIdList.includes(couponSupply.wayneActId)) {
            // 若有，执行第2步的全局推送消息
            // 获取 wayneActId 对应的新 couponSupply 数据
            const newCouponSupply = recallableWayneList.find(item => item.wayneActId === couponSupply.wayneActId);
            // 更新 couponSupply
            if (newCouponSupply) {
              onUpdateCouponSupply(newCouponSupply);
            }
            sendGlobalMessage(newCouponSupply);
          } else {
            executeJump();
          }
        } else {
          // 请求失败，执行跳转
          executeJump();
        }
        return;
      } catch (err) {
        console.error("[hpr] checkWayneSupplyRecallableError", err)
        reportError({
          name: '[CouponWall] checkWayneSupplyRecallableError',
          msg: err,
          category: 'couponError',
          level: Level.ERROR,
        })
        // 请求失败，执行跳转
        executeJump();
        return;
      }
    }

    // 默认执行跳转
    executeJump();
  };

  // 上报曝光埋点-券去使用按钮曝光
  const handleUseButtonExpose = () => {
    tracker?.moduleView(COUPON_WALL_BID_MV.USE_BUTTON, useButtonValLab);
  }

  // 渲染按钮
  const renderButton = () => {
    if (!shouldShowDetails) return null;
    if (couponStatus === CouponStatus.CAN_USE) {
      return (
        <Exposure onExpose={handleUseButtonExpose}>
          <div className="coupon-wall-card-btn" onClick={handleJump}>去使用</div>
        </Exposure>
      )
    }
    if (couponStatus === CouponStatus.RECEIVED || couponStatus === CouponStatus.EXPIRED) {
      return (
        <div className="coupon-wall-card-btn coupon-wall-card-btn-received">已领取</div>
      )
    }
    return null;
  }

  // 渲染单券样式按钮
  const renderSingleCouponCardButton = () => {
    // 未领取状态，显示一键领取按钮
    if (couponStatus === CouponStatus.NOT_RECEIVED) {
      return (
        <div className="coupon-wall-card-single-btn" onClick={onReceiveCoupon}>一键领取</div>
      )
    }
    if (couponStatus === CouponStatus.CAN_USE) {
      return (
        <div className="coupon-wall-card-single-btn" onClick={handleJump}>去使用</div>
      )
    }
    if (couponStatus === CouponStatus.RECEIVED || couponStatus === CouponStatus.EXPIRED) {
      return (
        <div className="coupon-wall-card-single-btn coupon-wall-card-single-btn-received">已领取</div>
      )
    }
    return null;
  }

  // 渲染倒计时
  const renderCountdown = () => {
    if (!shouldShowDetails) return null;
    if (couponStatus === CouponStatus.NOT_RECEIVED) return null;
    if (!currentTime || !couponEndTime) return null;
    if (couponStatus === CouponStatus.EXPIRED) return <span className='coupon-wall-card-time-expired'>已过期</span>;

    return (
      <Countdown 
        currentTime={currentTime} 
        expireTime={couponEndTime}
        onExpired={handleExpired}
      />
    )
  }

  // 上报点击埋点-券点击
  const handleClick = () => {
    tracker?.moduleClick(COUPON_WALL_BID_MC.COUPON, couponValLab);
  };

  // 上报曝光埋点-券曝光
  const handleExpose = () => {
    tracker?.moduleView(COUPON_WALL_BID_MV.COUPON, couponValLab);
  }

  // 单券样式
  const couponCard = style === CouponCardStyle.SINGLE
    ? ( // 单券样式
      <Exposure onExpose={handleExpose}>
        <div
          className={`coupon-wall-card coupon-wall-card-single ${isFestivalCoupon ? 'coupon-wall-card-festival' : ''}`}
          onClick={handleClick}
        >  
          {/* 左侧金额 & 使用门槛 */}
          <div className="coupon-wall-card-single-left">
            {/* 金额区域 */}
            <div className="coupon-wall-card-amount">
              <span className= {`coupon-wall-card-amount-unit ${useSmallFont ? 'coupon-wall-card-amount-unit-small' : ''}`}>¥</span>
              <span className={`coupon-wall-card-amount-value ${useSmallFont ? 'coupon-wall-card-amount-value-small' : ''}`}>
                {displayValue}
              </span>
            </div>
            {/* 使用门槛文案 */}
            <div className="coupon-wall-card-threshold">{priceLimitText}</div>
          </div>
          {/* 券标题 & 剩余时间 */}
          <div className="coupon-wall-card-single-middle">
            {/* 券标题 */}
            <div className="coupon-wall-card-name">{couponName}</div>
            {/* 剩余时间（若存在可领取的券，则不展示未领取券的倒计时） */}
            <div className="coupon-wall-card-time">{renderCountdown()}</div>
          </div>
          {/* 按钮区域 */}
          <div className={`coupon-wall-card-single-right ${autoReceiveCountdown && autoReceiveCountdown > 0 ? 'coupon-wall-card-single-right-countdown' : ''}`}>
            {renderSingleCouponCardButton()}
            {/* 自动领取倒计时 */}
            {autoReceiveCountdown && autoReceiveCountdown > 0 
              ? (
                <div className='coupon-wall-card-single-countdown'>
                  倒计时<span className='countdown-time'>{autoReceiveCountdown}</span>秒后自动领取
                </div>
              )
              : null
            }
          </div>
        </div>
      </Exposure>
    )
    : ( // 默认样式 or 紧凑样式
      <Exposure onExpose={handleExpose}>
        <div
          className={`coupon-wall-card coupon-wall-card-default ${enableFitFewCoupon ? 'enable-fit-few-coupon' : ''} ${isFestivalCoupon ? 'coupon-wall-card-festival' : ''} ${style === CouponCardStyle.COMPACT ? 'coupon-wall-card-compact' : ''}`}
          // @ts-ignore
          onClick={handleClick}
        >
          {/* 券类型文案 */}
          <div className="coupon-wall-card-name">{couponName}</div>
          {/* 金额区域 */}
          <div className="coupon-wall-card-amount">
            <span className= {`coupon-wall-card-amount-unit ${useSmallFont ? 'coupon-wall-card-amount-unit-small' : ''}`}>¥</span>
            <span className={`coupon-wall-card-amount-value ${useSmallFont ? 'coupon-wall-card-amount-value-small' : ''}`}>
              {displayValue}
            </span>
          </div>
          
          {/* 使用门槛文案（若存在可领取的券，则不展示未领取券的门槛） */}
          {shouldShowDetails && (
            <div className="coupon-wall-card-threshold">
              {priceLimitText}
            </div>
          )}
          
          {/* 按钮区域（若存在可领取的券，则不展示未领取券的按钮） */}
          {renderButton()}
          
          {/* 剩余时间（若存在可领取的券，则不展示未领取券的倒计时） */}
          { style === CouponCardStyle.DEFAULT && <div className="coupon-wall-card-time">{renderCountdown()}</div>}
        </div>
      </Exposure>
    );

  return (
    <>
      {/* 券卡片 */}
      {couponCard}
      {/* 唤端确认弹窗 */}
      <AppConfirmModal
        visible={showAppConfirmModal}
        onClose={handleCloseModal}
        url={operation?.jumpLink || ''}
      />
    </>
  );
}

export default CouponCard;
